import moment, { MomentFormatSpecification } from 'moment';
import { isInteger } from 'lodash';
import { isValidDate, isValidDateEditor } from './app.utilities';
import { ELSFormFieldValidationItem, ELSNumberFieldValidationItem } from '../models/els.models';
import { VALIDATOR } from '../constants/validator.constants';
import {
  hasValidUrlProtocol,
  isValidUrlFormat
} from '../pages/external-link-assignment-editor/external-link-assignment.utilities';
import { folderSetupMethod } from '../pages/course-builder/courseBuilder.constants';

export const validateRequiredAlpha2Chars = text => {
  const reg = /^[A-Z]{2}$/;
  return reg.test(text);
};

// letters, spaces, dashes and/or apostrophes only
export const validateFirstOrLastNameChars = text => {
  const reg = /^[A-Za-z\s'-]+$/;
  return reg.test(text);
};

export const validateRequiredAlphaNumeric2Chars = text => {
  const reg = /^[a-z0-9]{2}$/i;
  return reg.test(text);
};

export const validateRequiredAlphaNumericMax2Chars = text => {
  const reg = /^[a-z0-9]{1,2}$/i;
  return reg.test(text);
};

export const validateDateNotInFuture = (momentDate, now = moment()) => {
  const inPast = momentDate.diff(now, 'days') < 0;
  const sameDay = momentDate.diff(now, 'days') === 0 && momentDate.isSame(now, 'day');
  return inPast || sameDay;
};

export const validateEmailAddress = emailAddress => {
  const reg = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return reg.test(emailAddress);
};

export const asPromise = value => Promise.resolve(value);

export const validateNameFormat = (value: string, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_NAME_FORMAT,
    value,
    content: message,
    isValid: validateFirstOrLastNameChars(value)
  };
};

export const validateEmailAddressFormat = (value: string, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_EMAIL_FORMAT,
    value,
    content: message,
    isValid: validateEmailAddress(value)
  };
};

export const validateUrlFormat = (value: string, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_URL_FORMAT,
    value,
    content: message,
    isValid: isValidUrlFormat(value)
  };
};

export const validateUrlProtocol = (value: string, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_URL_PROTOCOL,
    value,
    content: message,
    isValid: isValidUrlFormat(value) ? hasValidUrlProtocol(value) : true
  };
};

export const validateStringLength = (value: string, maxLength: number, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_STRING_LENGTH,
    value,
    content: message,
    isValid: value ? value.length <= maxLength : true,
  };
};

export const validateStringMinLength = (value: string, minLength: number, message: string) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_STRING_MIN_LENGTH,
    value,
    content: message,
    isValid: value ? value.length >= minLength : false,
  };
};

export const validateValidDate = (value: Date | string, message: string, format?: MomentFormatSpecification) => (): ELSFormFieldValidationItem => {
  return {
    id: VALIDATOR.VALIDATE_DATE,
    value,
    content: message,
    isValid: isValidDateEditor(value, format),
  };
};
export const validateDateBefore = (value: string | Date, date: string | Date, message: string, id: string = VALIDATOR.VALIDATE_BEFORE_DATE) => (): ELSFormFieldValidationItem => {
  const isValid = moment(value).isAfter(moment(date));
  return {
    id,
    value,
    content: message,
    isValid,
  };
};

export const validateValidDateTime = (value: string | Date, originalInput: string, message: string, format?: MomentFormatSpecification) => (): ELSFormFieldValidationItem => {
  const isDateAndDateStrValid = isValidDate(value, format) && isValidDate(originalInput, format);
  return {
    id: VALIDATOR.VALIDATE_DATE,
    value,
    content: message,
    isValid: originalInput === undefined ? isValidDate(value, format) : isDateAndDateStrValid,
  };
};

export const validateWeeks = (value: number, message: string, _folderSetupMethod: string) => (): ELSNumberFieldValidationItem => {
  return {
    id: VALIDATOR.VALID_WEEKS,
    value,
    content: message,
    isValid: _folderSetupMethod === folderSetupMethod.STANDARD ? isInteger(value) && value > 0 : isInteger(value) && value >= 0,
  };
};

export const validateDateForPushDate = (id: string, value: number | string, message: string) => (): ELSNumberFieldValidationItem => {
  const val = Number(value);
  return {
    id,
    value: val,
    content: message,
    isValid: value !== '' && isInteger(val) && val > -1 && val < 1000,
  };
};

export const isInvalidLength = (value: string, maxLength: number): boolean => {
  if (!value || value.length === 0) {
    return true;
  }
  return value && value.length > maxLength;
};
